import { useState } from 'react';
import { Seo, Mark, Container } from '../components/marketing/Primitives';
import { db } from '../config/firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';

const audiences = [
  'A certification board',
  'A medical school or residency program',
  'A licensure or certification candidate',
  'An internationally trained physician or sponsor',
  'A self-directed learner',
  'Other',
];

const inputClass =
  'w-full rounded border border-hairline bg-surface px-4 py-3 text-sm text-bright placeholder-faint transition-colors focus:border-seal';

export default function ContactPage() {
  const [form, setForm] = useState({ name: '', email: '', organization: '', audience: '', message: '' });
  const [status, setStatus] = useState('idle'); // idle | sending | sent | error

  function update(field) {
    return (e) => setForm((f) => ({ ...f, [field]: e.target.value }));
  }

  async function handleSubmit() {
    if (!form.name || !form.email || !form.message) {
      setStatus('error');
      return;
    }
    setStatus('sending');
    try {
      await addDoc(collection(db, 'demo_requests'), {
        ...form,
        source: 'contact-page',
        createdAt: serverTimestamp(),
      });
      setStatus('sent');
      setForm({ name: '', email: '', organization: '', audience: '', message: '' });
    } catch (err) {
      // Fall back to email if the write fails for any reason.
      const subject = encodeURIComponent('Demo request from edaisystems.com');
      const body = encodeURIComponent(
        `Name: ${form.name}\nEmail: ${form.email}\nOrganization: ${form.organization}\nI am: ${form.audience}\n\n${form.message}`
      );
      window.location.href = `mailto:john@edaisystems.com?subject=${subject}&body=${body}`;
      setStatus('idle');
    }
  }

  return (
    <>
      <Seo
        title="Request a demo"
        description="Talk to EdAI Systems about your board, your school, your program, or your own preparation."
      />

      <section className="border-b border-hairline bg-surface">
        <Container className="py-16 md:py-20">
          <Mark>Contact</Mark>
          <h1 className="mt-4 max-w-[20ch] font-serif text-4xl leading-tight md:text-5xl">Request a demo.</h1>
          <p className="mt-5 max-w-measure leading-relaxed text-slate">
            Tell us who you are and what you want to see. Demos are live and specific: your board’s content, your
            school’s curriculum, your exam, your route.
          </p>
        </Container>
      </section>

      <section className="py-16 md:py-20">
        <Container>
          <div className="grid gap-14 lg:grid-cols-[1.5fr_1fr]">
            <div className="max-w-measure-wide">
              {status === 'sent' ? (
                <div className="rounded border border-hairline bg-surface p-8">
                  <p className="font-serif text-2xl">Request received.</p>
                  <p className="mt-3 leading-relaxed text-slate">
                    Thank you. We will reply from john@edaisystems.com, usually within one business day.
                  </p>
                </div>
              ) : (
                <div className="space-y-5">
                  <div className="grid gap-5 sm:grid-cols-2">
                    <div>
                      <label htmlFor="name" className="mb-1.5 block text-sm font-medium">
                        Name
                      </label>
                      <input id="name" type="text" value={form.name} onChange={update('name')} className={inputClass} placeholder="Your name" />
                    </div>
                    <div>
                      <label htmlFor="email" className="mb-1.5 block text-sm font-medium">
                        Email
                      </label>
                      <input id="email" type="email" value={form.email} onChange={update('email')} className={inputClass} placeholder="you@example.org" />
                    </div>
                  </div>
                  <div className="grid gap-5 sm:grid-cols-2">
                    <div>
                      <label htmlFor="organization" className="mb-1.5 block text-sm font-medium">
                        Organization (optional)
                      </label>
                      <input id="organization" type="text" value={form.organization} onChange={update('organization')} className={inputClass} placeholder="Board, school, or program" />
                    </div>
                    <div>
                      <label htmlFor="audience" className="mb-1.5 block text-sm font-medium">
                        I am
                      </label>
                      <select id="audience" value={form.audience} onChange={update('audience')} className={inputClass}>
                        <option value="">Choose one</option>
                        {audiences.map((a) => (
                          <option key={a} value={a}>
                            {a}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label htmlFor="message" className="mb-1.5 block text-sm font-medium">
                      What would you like to see?
                    </label>
                    <textarea id="message" rows="5" value={form.message} onChange={update('message')} className={inputClass} placeholder="The product you want to see, and anything we should prepare." />
                  </div>
                  {status === 'error' && (
                    <p className="text-sm text-slate">Name, email, and a message are needed to send the request.</p>
                  )}
                  <button type="button" onClick={handleSubmit} disabled={status === 'sending'} className="btn-primary disabled:opacity-60">
                    {status === 'sending' ? 'Sending' : 'Send request'}
                  </button>
                </div>
              )}
            </div>
            <aside className="border-hairline lg:border-l lg:pl-10">
              <h2 className="font-serif text-lg">Reach us directly</h2>
              <p className="mt-3 text-sm leading-relaxed text-slate">
                <a href="mailto:john@edaisystems.com" className="link-quiet">
                  john@edaisystems.com
                </a>
              </p>
              <p className="mt-6 text-sm leading-relaxed text-slate">
                EdAI Systems, Inc.
                <br />
                677 Ala Moana Blvd, Suite 1011
                <br />
                Honolulu, HI 96813
              </p>
            </aside>
          </div>
        </Container>
      </section>
    </>
  );
}
